import { Box, CircularProgress, Typography } from '@mui/material'
import type { Embed, Report } from 'powerbi-client'
import { EmbedType, PowerBIEmbed } from 'powerbi-client-react'
import type { IBasicFilter } from 'powerbi-models'
import { BackgroundType, FilterType, FiltersOperations, TokenType, TraceType } from 'powerbi-models'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { UnauthorizedError } from '../../auth/UnauthorizedError'
import { NotFoundError } from '../../errors/NotFoundError'
import { clearAuth } from '../../store/authSlice'
import { useAppDispatch } from '../../store/store.hooks'
import { CustomEmbedSettings } from '../../types'
import { logger } from '../../utils/logger'
import { fetchAdminReportParams, fetchReportParams } from '../services/ReportsApi'
import { useAdminReportParams, useReportParams } from '../services/ReportsHooks'

interface Props {
  reportId: string
  workspaceId: string
  settings?: CustomEmbedSettings
  isAdminOnly?: boolean
}

// Compared against IError.message, not errorCode — not exported by powerbi-client's typings.
const TOKEN_EXPIRED_ERROR_MESSAGE = 'TokenExpired'

const REFRESH_BUFFER_MS = 5 * 60 * 1000
const MIN_REFRESH_DELAY_MS = 5 * 1000

// Power BI resolves table and column translations on an exact culture-name match and silently falls
// back to the model's default object names otherwise, so a bare 'fr' or a browser tag such as 'fr-FR'
// would render the report untranslated. Measures alone tolerate a nearest-match fallback.
const POWER_BI_CULTURES: Record<string, string> = { en: 'en-CA', fr: 'fr-CA' }
const DEFAULT_POWER_BI_CULTURE = 'en-CA'

function toPowerBiCulture(language: string) {
  return POWER_BI_CULTURES[language.slice(0, 2).toLowerCase()] ?? DEFAULT_POWER_BI_CULTURE
}

// Translated data values live as one row per culture; a bilingual model exposes a single
// Languages[CultureName] to select them (contract: PmpTeamMate Docs/architecture/downtimes/powerbi-localization.md).
// Models without that table silently accept and ignore the filter.
function buildLanguageFilter(culture: string): IBasicFilter {
  return {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: { table: 'Languages', column: 'CultureName' },
    operator: 'In',
    values: [culture],
    filterType: FilterType.Basic,
    requireSingleSelection: true,
  }
}

// Power BI page tabs aren't localizable, so translated reports ship their own page navigation and
// flag it with a page of this display name; the native tabs are then hidden.
const OWN_PAGE_NAVIGATION_MARKER_PAGE = 'PortalNavigation'

async function hideNativePageNavigationIfReportHasOwn(report: Report) {
  const pages = await report.getPages()
  if (pages.some((page) => page.displayName === OWN_PAGE_NAVIGATION_MARKER_PAGE)) {
    await report.updateSettings({ navContentPaneEnabled: false })
  }
}

const ReportViewer = ({ reportId, workspaceId, settings, isAdminOnly }: Props) => {
  const { t, i18n } = useTranslation()
  const powerBiCulture = toPowerBiCulture(i18n.language)
  const dispatch = useAppDispatch()
  const { reportParams, error } = isAdminOnly
    ? useAdminReportParams(workspaceId, reportId)
    : useReportParams(workspaceId, reportId)

  const embedRef = useRef<Embed | null>(null)
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const isRefreshingRef = useRef(false)
  const currentExpirationMsRef = useRef<number | null>(null)
  const [isRecovering, setIsRecovering] = useState(false)
  const [recoveryFailed, setRecoveryFailed] = useState(false)

  const fetchParams = useCallback(
    () => (isAdminOnly ? fetchAdminReportParams(workspaceId, reportId) : fetchReportParams(workspaceId, reportId)),
    [isAdminOnly, workspaceId, reportId],
  )

  const refreshToken = useCallback(
    async function refreshToken() {
      if (isRefreshingRef.current) return
      isRefreshingRef.current = true
      setIsRecovering(true)
      try {
        const freshParams = await fetchParams()
        await embedRef.current?.setAccessToken(freshParams.embedToken.token)
        setRecoveryFailed(false)
        const expirationMs = new Date(freshParams.embedToken.expiration).getTime()
        currentExpirationMsRef.current = expirationMs
        const delay = Math.max(expirationMs - Date.now() - REFRESH_BUFFER_MS, MIN_REFRESH_DELAY_MS)
        refreshTimeoutRef.current = setTimeout(refreshToken, delay)
      } catch (refreshError) {
        logger.error('Failed to refresh Power BI embed token', refreshError)
        if (refreshError instanceof UnauthorizedError) {
          dispatch(clearAuth())
        } else {
          setRecoveryFailed(true)
        }
      } finally {
        isRefreshingRef.current = false
        setIsRecovering(false)
      }
    },
    [fetchParams, dispatch],
  )

  // Ref indirection: powerbi-client-react dedupes event handlers by source text, not
  // identity, so a plain closure over refreshToken could go stale.
  const refreshTokenRef = useRef(refreshToken)
  useEffect(() => {
    refreshTokenRef.current = refreshToken
  }, [refreshToken])

  const applyLanguageFilter = useCallback(async () => {
    const report = embedRef.current as Report | null
    if (!report) return
    try {
      await report.updateFilters(FiltersOperations.Replace, [buildLanguageFilter(powerBiCulture)])
    } catch (filterError) {
      logger.warn('Languages filter not applied', filterError)
    }
  }, [powerBiCulture])

  const applyLanguageFilterRef = useRef(applyLanguageFilter)
  useEffect(() => {
    applyLanguageFilterRef.current = applyLanguageFilter
  }, [applyLanguageFilter])

  useEffect(() => {
    if (!reportParams) return undefined
    setRecoveryFailed(false)
    const expirationMs = new Date(reportParams.embedToken.expiration).getTime()
    currentExpirationMsRef.current = expirationMs
    const delay = Math.max(expirationMs - Date.now() - REFRESH_BUFFER_MS, MIN_REFRESH_DELAY_MS)
    refreshTimeoutRef.current = setTimeout(refreshToken, delay)
    return () => clearTimeout(refreshTimeoutRef.current)
  }, [reportParams, refreshToken])

  // Background tabs throttle setTimeout, so re-check when the tab becomes visible again.
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.hidden) return
      const expirationMs = currentExpirationMsRef.current
      if (expirationMs !== null && Date.now() >= expirationMs - REFRESH_BUFFER_MS) {
        refreshTokenRef.current()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  if (error instanceof NotFoundError) {
    return <Navigate to="/" />
  }

  if (!reportParams) {
    return null
  }

  if (recoveryFailed) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography align="center">{t('reports.refreshReportFailed')}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      {isRecovering && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <CircularProgress size={32} />
          <Typography align="center">{t('reports.refreshingReport')}</Typography>
        </Box>
      )}
      <PowerBIEmbed
        // Keyed on the culture because Power BI ignores locale changes after load — switching language
        // requires a fresh iframe. Never key on the token: refreshes must stay in place (setAccessToken).
        key={powerBiCulture}
        embedConfig={{
          type: EmbedType.Report,
          id: reportParams.embedReport.id,
          embedUrl: reportParams.embedReport.embedUrl,
          accessToken: reportParams.embedToken.token,
          tokenType: TokenType.Embed,
          settings: {
            panes: {
              filters: {
                expanded: false,
                visible: false,
              },
            },
            background: BackgroundType.Default,
            ...settings,
            // Spread after `...settings` so a future CustomEmbedSettings field can never silently
            // override the language the report is embedded in.
            localeSettings: {
              language: powerBiCulture,
              formatLocale: powerBiCulture,
            },
          },
        }}
        eventHandlers={
          new Map([
            [
              // Runs before the first render, so the language filter lands before any data is shown.
              'loaded',
              function () {
                applyLanguageFilterRef.current()
                hideNativePageNavigationIfReportHasOwn(embedRef.current as Report).catch((navError) =>
                  logger.warn('Page navigation marker not checked', navError),
                )
              },
            ],
            [
              'error',
              function (event) {
                logger.error('Report error', event?.detail)
                const reportError = event?.detail
                if (reportError?.level === TraceType.Fatal && reportError?.message === TOKEN_EXPIRED_ERROR_MESSAGE) {
                  refreshTokenRef.current()
                }
              },
            ],
          ])
        }
        getEmbeddedComponent={(embeddedComponent) => {
          embedRef.current = embeddedComponent
        }}
        cssClassName="report-viewer"
      />
    </Box>
  )
}

export default ReportViewer
